import { z } from "zod";

/** Data contract produced by `core.pipeline.export_web` (see docs/DATA_CONTRACT.md). */
export const ViewHintSchema = z.object({
	az: z.number(),
	el: z.number(),
	min: z.number().positive(),
});

export const PartSchema = z.object({
	name: z.string().min(1),
	ref: z.string(),
});

export const StepSchema = z.object({
	code: z.string().regex(/^E\d{2}$/),
	num: z.number().int().nonnegative(),
	f0: z.number().int().positive(),
	f1: z.number().int().positive(),
	title: z.string(),
	detail: z.string(),
	parts: z.array(PartSchema),
	focus: z.array(z.string()),
	view: ViewHintSchema.nullable().optional(),
	click: z.boolean().optional(),
});

export const ProductSchema = z.object({
	slug: z.string().optional(),
	name: z.string(),
	article: z.string().optional(),
	size_cm: z.array(z.number()).optional(),
	notice: z.string().optional(),
	lang: z.string().optional(),
	fps: z.number().positive(),
	frame_end: z.number().int().positive(),
	model: z.string().default("model.glb"),
});

/** [frame, hidden] keys, sorted by frame. */
export const VisibilityKeySchema = z.tuple([
	z.number().int().nonnegative(),
	z.boolean(),
]);

export const StepsDataSchema = z
	.object({
		version: z.number().optional(),
		product: ProductSchema,
		steps: z.array(StepSchema).min(1),
		labels: z.record(z.string(), z.string()).default({}),
		visibility: z.record(z.string(), z.array(VisibilityKeySchema)).default({}),
	})
	.superRefine((d, ctx) => {
		d.steps.forEach((s, i) => {
			if (s.f1 <= s.f0) {
				ctx.addIssue({
					code: "custom",
					path: ["steps", i],
					message: `${s.code}: f1 must be > f0`,
				});
			}
			const next = d.steps[i + 1];
			if (next && next.f0 <= s.f1) {
				ctx.addIssue({
					code: "custom",
					path: ["steps", i + 1],
					message: `${next.code} overlaps ${s.code}`,
				});
			}
		});
	});

/**
 * Generation steps of a product, mirror of `GenerationStatus` in core/src/core/models/product.py
 * (keep the order: it is the order of the pipeline).
 */
export const GENERATION_STATUSES = [
	"pending",
	"queued",
	"fetching_guide",
	"analyzing_guide",
	"modeling",
	"checking_collisions",
	"exporting",
	"publishing",
	"ready",
	"failed",
] as const;

/** A status the API added before the webapp knows it is shown as "unknown" instead of failing. */
export const ProductStatusSchema = z
	.enum([...GENERATION_STATUSES, "unknown"])
	.catch("unknown");

/** Steps of a running generation, from the request to the publication. */
export const GENERATION_STEPS = [
	"queued",
	"fetching_guide",
	"analyzing_guide",
	"modeling",
	"checking_collisions",
	"exporting",
	"publishing",
] as const satisfies readonly ProductStatus[];

export const isGenerating = (status: ProductStatus): boolean =>
	(GENERATION_STEPS as readonly ProductStatus[]).includes(status);

/** Core API (`GET /products`, `GET /products/{slug}`), see core/src/core/routers/products.py. */
export const ProductSummarySchema = z.object({
	slug: z.string().min(1),
	ikea_id: z.string(),
	name: z.string(),
	designation: z.string(),
	category: z.string(),
	product_url: z.string(),
	assembly_guide_url: z.string().nullable(),
	/** Source URL of the product photo (CSV column `image`, or the IKEA page). */
	image_url: z.string().nullable().default(null),
	/** The photo to show: our copy in the bucket (presigned, changes at every call), else image_url. */
	photo_url: z.string().nullable().default(null),
	popularity_rank: z.number().int().nullable(),
	generation_status: ProductStatusSchema,
	generation_error: z.string().nullable(),
	generation_updated_at: z.string().nullable(),
});

export const ProductPageSchema = z.object({
	items: z.array(ProductSummarySchema),
	total: z.number().int().nonnegative(),
	limit: z.number().int(),
	offset: z.number().int(),
});

/** Presigned URLs of the viewer files; `version` changes only when the files change. */
export const ProductViewerSchema = z.object({
	model_url: z.string().url(),
	steps_url: z.string().url(),
	version: z.string().min(1),
});

export const ProductDetailSchema = ProductSummarySchema.extend({
	viewer: ProductViewerSchema.nullable(),
});

/** Core API, authentication (`/auth/*`), see core/src/core/routers/auth.py. */
export const UserSchema = z.object({
	id: z.string(),
	email: z.string(),
	name: z.string().nullable(),
});

/** Sign-in methods this deployment offers (a provider is offered once configured). */
export const ProvidersSchema = z.object({
	google: z.boolean(),
	apple: z.boolean(),
	email: z.boolean(),
});

/** Outcome of a magic-link sign-in: the user and the page to open. */
export const SignedInSchema = z.object({
	user: UserSchema,
	next: z.string(),
});

export type ViewHint = z.infer<typeof ViewHintSchema>;
export type Part = z.infer<typeof PartSchema>;
export type Step = z.infer<typeof StepSchema>;
export type Product = z.infer<typeof ProductSchema>;
export type StepsData = z.infer<typeof StepsDataSchema>;
export type ProductStatus = z.infer<typeof ProductStatusSchema>;
export type ProductSummary = z.infer<typeof ProductSummarySchema>;
export type ProductPage = z.infer<typeof ProductPageSchema>;
export type ProductViewer = z.infer<typeof ProductViewerSchema>;
export type ProductDetail = z.infer<typeof ProductDetailSchema>;
export type User = z.infer<typeof UserSchema>;
export type Providers = z.infer<typeof ProvidersSchema>;
export type SignedIn = z.infer<typeof SignedInSchema>;
export type VisibilityKey = z.infer<typeof VisibilityKeySchema>;
